import React, { useState, useEffect, useMemo } from 'react';

const PRAYER_ORDER = ['shacharit', 'mincha', 'maariv', 'other'];
const PRAYER_LABEL = { shacharit: 'שחרית', mincha: 'מנחה', maariv: 'מעריב' };

const PRAYER_ACCENT = { shacharit: '#1e3a5f', mincha: '#b8860b', maariv: '#374151', other: '#64748b' };

const toMinutes = (t) => {
  if (!t || t === '--:--') return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const getHebrewDay = () => {
  try { return parseInt(new Intl.DateTimeFormat('he-u-ca-hebrew', { day: 'numeric' }).format(new Date())); }
  catch { return 0; }
};

const isOverrideActive = (days, dow, isRoshChodesh) =>
  (days || []).includes(dow) || (isRoshChodesh && (days || []).includes('roshChodesh'));

const StatusBox = ({ msg, isError }) => (
  <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', direction: 'rtl', fontSize: '1.125rem', color: isError ? '#c0392b' : '#94a3b8', backgroundColor: '#fafafa' }}>
    {msg}
  </div>
);

const Spotlight = ({ processedCustomTimes, loading, error, title = 'תפילות יום חול', showTitle = true }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const dow = now.getDay();
  const hebrewDay = getHebrewDay();
  const isRoshChodesh = hebrewDay === 1 || hebrewDay === 30;
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const adjNowMin = nowMin < 180 ? nowMin + 1440 : nowMin;

  const { nextEntry, otherEntries } = useMemo(() => {
    if (!processedCustomTimes?.length) return { nextEntry: null, otherEntries: [] };

    const entries = [];
    const sorted = [...processedCustomTimes].sort((a, b) =>
      PRAYER_ORDER.indexOf(a.prayer || 'other') - PRAYER_ORDER.indexOf(b.prayer || 'other')
    );

    for (const ct of sorted) {
      const label = ct.label || PRAYER_LABEL[ct.prayer] || '';
      const activeOverride = (ct.resolvedOverrides || []).find(ov => isOverrideActive(ov.days, dow, isRoshChodesh));

      if (ct.type === 'recurring') {
        if (activeOverride) {
          const m = toMinutes(activeOverride.resolvedTime);
          if (m >= 0) entries.push({ label, time: activeOverride.resolvedTime, timeMin: m, prayer: ct.prayer, daysLabel: ct.daysLabel, isActiveToday: ct.isActiveToday });
        } else {
          for (const slot of (ct.resolvedSlots || [])) {
            const m = toMinutes(slot);
            if (m >= 0) entries.push({ label, time: slot, timeMin: m, prayer: ct.prayer, daysLabel: ct.daysLabel, isActiveToday: ct.isActiveToday });
          }
        }
      } else {
        const effectiveTime = activeOverride ? activeOverride.resolvedTime : ct.resolvedTime;
        const m = toMinutes(effectiveTime);
        if (m >= 0) entries.push({ label, time: effectiveTime, timeMin: m, prayer: ct.prayer, daysLabel: ct.daysLabel, isActiveToday: ct.isActiveToday });
      }
    }

    entries.sort((a, b) => a.timeMin - b.timeMin);
    const nextIdx = entries.findIndex(e => e.timeMin >= adjNowMin);
    if (nextIdx === -1) return { nextEntry: null, otherEntries: entries };

    return {
      nextEntry: entries[nextIdx],
      otherEntries: entries.filter((_, i) => i !== nextIdx),
    };
  }, [processedCustomTimes, dow, isRoshChodesh, adjNowMin]);

  if (loading) return <StatusBox msg="טוען..." />;
  if (error) return <StatusBox msg={error} isError />;
  if (!processedCustomTimes?.length) return <StatusBox msg="אין זמני תפילה" />;

  const nowStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const accent = nextEntry ? (PRAYER_ACCENT[nextEntry.prayer] || '#1e3a5f') : '#94a3b8';

  return (
    <div style={{ width: '100%', height: '100%', direction: 'rtl', display: 'flex', flexDirection: 'column', backgroundColor: '#ffffff', boxSizing: 'border-box', overflow: 'hidden' }}>
      {showTitle && (
        <div style={{ padding: '0.875rem 1.5rem', borderBottom: '1px solid #e2e8f0', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontWeight: 700, fontSize: '1.25rem', color: '#1e3a5f' }}>{title}</span>
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{nowStr}</span>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '1rem', gap: '0.875rem' }}>

        {/* Spotlight card */}
        <div style={{
          flexShrink: 0,
          borderRadius: '14px',
          border: `1px solid ${accent}20`,
          borderTop: `4px solid ${accent}`,
          backgroundColor: '#fafafa',
          padding: '1.25rem 1.5rem',
          display: 'flex', flexDirection: 'column', gap: '0.25rem',
        }}>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
            {nextEntry ? 'המניין הבא' : 'אין תפילות נוספות'}
          </span>
          {nextEntry ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', marginTop: '4px' }}>
              <span style={{ fontSize: '1.5rem', fontWeight: 800, color: accent }}>
                {nextEntry.label}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                <span style={{
                  fontSize: '2.5rem',
                  fontWeight: 800,
                  color: accent,
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  fontVariantNumeric: 'tabular-nums lining-nums',
                  direction: 'ltr',
                  letterSpacing: '-0.04em',
                  lineHeight: 1,
                }}>
                  {nextEntry.time}
                </span>
                {nextEntry.daysLabel && (
                  <span style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 500 }}>{nextEntry.daysLabel}</span>
                )}
              </div>
            </div>
          ) : (
            <span style={{ fontSize: '1rem', color: '#64748b', marginTop: '4px' }}>כל התפילות עברו</span>
          )}
        </div>

        {/* Rest */}
        {otherEntries.length > 0 && (
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: '0' }}>
            <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#cbd5e1', letterSpacing: '0.08em', textTransform: 'uppercase', flexShrink: 0, marginBottom: '0.5rem' }}>
              זמנים נוספים
            </span>
            <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              {otherEntries.map((entry, i) => {
                const isPast = entry.timeMin < nowMin;
                const entryAccent = PRAYER_ACCENT[entry.prayer] || '#64748b';
                const isLast = i === otherEntries.length - 1;
                return (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '0.5rem 0.75rem',
                    borderBottom: isLast ? 'none' : '1px solid #f8fafc',
                    opacity: isPast ? 0.35 : 1,
                    flexShrink: 0,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '3px', height: '18px', borderRadius: '2px', backgroundColor: entryAccent, flexShrink: 0 }} />
                      <span style={{ fontSize: '1rem', fontWeight: 600, color: '#1e293b' }}>{entry.label}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                      {entry.daysLabel && (
                        <span style={{ fontSize: '0.6rem', color: '#94a3b8', fontWeight: 500 }}>{entry.daysLabel}</span>
                      )}
                      <span style={{
                        fontSize: '1.25rem',
                        fontWeight: 700,
                        color: isPast ? '#cbd5e1' : '#0f172a',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontVariantNumeric: 'tabular-nums lining-nums',
                        direction: 'ltr',
                        letterSpacing: '-0.03em',
                      }}>
                        {entry.time}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Spotlight;
